import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'
import { ActivityQueriesFilter } from 'types/activity'
import { ACTION_OPTIONS } from '../constants'

interface ActivityFilterContextType {
  filter: ActivityQueriesFilter
  updateFilter: (newFilter: Partial<ActivityQueriesFilter>) => void
  resetFilter: () => void
  isLoading: boolean
  setLoading: (loading: boolean) => void
}

// default excluded actions - with guard for test environment
const getDefaultExcludedActions = () => {
  return (ACTION_OPTIONS || [])
    .filter(
      (option) =>
        option.value === 'Page View' ||
        option.value.toLowerCase().includes('noyo') ||
        option.value === 'VCN Card Retrieved'
    )
    .map((option) => option.value)
}

const defaultFilter: ActivityQueriesFilter = {
  search: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  clientIds: undefined,
  actorIds: undefined,
  targetIds: undefined,
  categories: undefined,
  excludeActions: getDefaultExcludedActions(),
  page: undefined,
  limit: undefined,
  sort: undefined,
  order: undefined,
}

const ActivityFilterContext = createContext<
  ActivityFilterContextType | undefined
>(undefined)

export const useActivityFilter = () => {
  const context = useContext(ActivityFilterContext)
  if (!context) {
    throw new Error(
      'useActivityFilter must be used within an ActivityFilterProvider'
    )
  }
  return context
}

export const ActivityFilterProvider = ({
  children,
  initialFilter,
  onFilterChange,
}: {
  children: ReactNode
  initialFilter?: ActivityQueriesFilter
  onFilterChange?: (filter: ActivityQueriesFilter) => void
}) => {
  /* --------------------------------- States --------------------------------- */
  const [filter, setFilter] = useState<ActivityQueriesFilter>(() => {
    // Merge initialFilter with defaultFilter to preserve default values like excludeActions
    return {
      ...defaultFilter,
      ...initialFilter,
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const setLoading = useCallback(
    (loading: boolean) => setIsLoading(loading),
    []
  )

  /* --------------------------------- Methods -------------------------------- */
  const updateFilter = useCallback(
    (newFilter: Partial<ActivityQueriesFilter>) => {
      setFilter((prevFilter) => {
        const updatedFilter = {
          ...prevFilter,
          ...newFilter,
        }

        // Call the onFilterChange callback if provided
        if (onFilterChange) {
          onFilterChange(updatedFilter)
        }

        return updatedFilter
      })
    },
    [onFilterChange]
  )

  const resetFilter = useCallback(() => {
    // Merge initialFilter with defaultFilter to preserve default values like excludeActions
    const resetValue = {
      ...defaultFilter,
      ...initialFilter,
    }
    setFilter(resetValue)
    if (onFilterChange) {
      onFilterChange(resetValue)
    }
  }, [initialFilter, onFilterChange])

  /* ---------------------------------- Doms ---------------------------------- */
  return (
    <ActivityFilterContext.Provider
      value={{ filter, updateFilter, resetFilter, isLoading, setLoading }}
    >
      {children}
    </ActivityFilterContext.Provider>
  )
}
